import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { returnToPreviousMacroWizardPath } from '../../../../actions/searchActions';
import { getCurrentDecisionNodeContent, getSimplrAccountIdOfSelectedTicket } from '../../../../extractFromState';
import {
  SearchResultsContainer,
  HeaderContainer,
  Title,
  SubTitle,
  ResultsSection,
  SearchOrdersBox,
  SearchInputContainer,
  SpaceHolder,
  Container,
  SearchOrderContent,
  SearchAndResults,
  NoResultsWrapper,
} from './DTOrderSearch.styles';
import { BackButton } from '../../../common/buttonsComponents';

import {
  chooseDefaultSearchValue,
  getOrderSearchQuery,
  getOrdersSearchProperties,
  getSelectedOrderSearchAction,
  getDynamicOrderSearchValues,
  getDefaultEmail,
  isEmailInDynamicIntegrationSearchInput,
  isEmailOnlyDynamicIntegrationSearchInput,
} from '../../../../selectors/tools';
import {
  clearExtraLastMileInfo,
  clearSelectedItems,
  resetShowConfirmationPage,
  selectOrder,
  selectOrderingSystem,
  setSearchOrdersAction,
  setSearchOrdersQuery,
  setDynamicOrderSearchValue,
} from '../../../../actions/tools/OrdersSearchActions';
import { SearchPageSelect } from './Common/SearchPageSelect/SearchPageSelect';
import { trackOrderSearchQuerySubmitted, trackOrderSearchSetAction } from '../../../../analytics/searchEventAnalytics';
import { chooseOrderingSystemForSelectedAction, emailRegex } from '../../../../util';
import * as searchActions from '../../../../constants/searchActions';
import { displayPopupMessageInformation } from '../../../tools/SearchPage/searchUtils';
import { trimObjectValues } from 'util/formatUtil';
import { LoadingStateWrapper } from '../../../common/LoadingStateWrapper/LoadingStateWrapper';
import { LoadingStateContextConsumer } from '../../../common/LoadingStateWrapper/LoadingStateContext';
import SearchIntro from '../../../tools/SearchPage/SearchIntro/SearchIntro';
import { setLoading as setLoadingAction } from '../../../../actions';
import { shopifyGetCustomerOrders, shopifyGetOrder } from '../../../../actions/tools/ShopifyActions';
import { amazonGetOrderByNumber } from '../../../../actions/tools/AmazonActions';
import {
  searchRbiOrdersByEmail,
  searchRbiOrdersByCardId,
  searchRbiOrdersById,
} from '../../../../actions/tools/RBIActions';
import { searchSalesforceOrdersByEmail, searchSalesforceOrdersById } from '../../../../actions/tools/SalesforceActions';
import { bigCommerceFindOrders } from '../../../../actions/tools/BigCommerceActions';
import { revolveFindOrders } from '../../../../actions/tools/RevolveActions';
import { searchOrders } from '../../../../actions/tools/CommonAsyncOrderActions';
import NoSearchResults from './Common/NoSearchResults/NoSearchResults';
import { SearchResults } from './Common/SearchResults/SearchResults';
import SearchPageNextButton from './Common/SearchPageNextButton/SearchPageNextButton';
import DTSearchInput from '../../Common/DTSearchInput/DTSearchInput';
import DTDynamicSearchBar from '../../Common/DTDynamicSearchBar/DTDynamicSearchBar';
import { Loader } from '../../../common/Loader/Loader';
import { handleError } from 'services/ErrorService/ErrorService';
import { ActionsButtonsWrapper } from '../../DecisionTree.styles';
import { CUSTOMER_EMAIL_SEARCH_INPUT } from 'constants/dynamicIntegrations';

export const DTOrderSearchView = (props) => {
  const { onNextButtonClick, isNextButtonActive, hideNextButton = false } = props;

  const dispatch = useDispatch();

  const content = useSelector(getCurrentDecisionNodeContent);
  const selectedSearchAction = useSelector(getSelectedOrderSearchAction);
  const defaultSearchValue = useSelector(chooseDefaultSearchValue);
  const orderSearchQuery = useSelector(getOrderSearchQuery);
  const setLoading = useCallback((loading) => dispatch(setLoadingAction(loading, 'searchPage')), [dispatch]);
  const ordersSearch = useSelector(getOrdersSearchProperties);
  const simplrAccountPtrId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const isLoading = useSelector((state) => state.ui.loading.isLoading && state.ui.loading.component === 'searchPage');
  const isDynamicIntegrationSelected = selectedSearchAction?.includes(searchActions.COMMON_ORDER_DYNAMIC_INPUT);
  const dynamicOrderSearchValues = useSelector(getDynamicOrderSearchValues);
  const defaultEmail = useSelector(getDefaultEmail);
  const isEmailRequiredForDynamicIntegration = useSelector(isEmailInDynamicIntegrationSearchInput);
  const isEmailOnlyInputForDynamicIntegration = useSelector(isEmailOnlyDynamicIntegrationSearchInput);
  const isDynamicIntegrationEmailPopulated = !!dynamicOrderSearchValues?.customerEmail;

  const handleSearchOrders = useCallback(() => {
    dispatch(resetShowConfirmationPage());

    // trim if available or revert to empty string
    const searchStr = (orderSearchQuery && orderSearchQuery.trim()) || '';

    trackOrderSearchQuerySubmitted({ selectedSearchAction, searchQuery: searchStr });

    let query = null;

    switch (selectedSearchAction) {
      case searchActions.SHOPIFY_ORDER_NUMBER:
        if (!searchStr) {
          displayPopupMessageInformation('Please enter an order number');
          break;
        }
        query = {
          name: searchStr,
          status: 'any',
        };
        setLoading(true);

        return dispatch(shopifyGetOrder(query));
      case searchActions.SHOPIFY_EMAIL:
        if (!emailRegex.test(searchStr)) {
          displayPopupMessageInformation('Please enter a valid email address');
          break;
        }
        query = {
          status: 'any',
          email: searchStr,
        };
        setLoading(true);

        return dispatch(shopifyGetCustomerOrders(query));
      case searchActions.SHOPIFY_PHONE_NUMBER:
        // remove non-digit characters from phone number prior to regex check
        const phoneNumberSearchStr = searchStr.replace(/\D/g, '');

        if (!/^[0-9]{11}$/.test(phoneNumberSearchStr)) {
          displayPopupMessageInformation('Phone number must be 11 digits, i.e. "18001234567"');
          break;
        }
        query = {
          query: `phone:${phoneNumberSearchStr}`,
          status: 'any',
        };
        setLoading(true);

        return dispatch(shopifyGetCustomerOrders(query));
      case searchActions.SHOPIFY_FULL_NAME:
        const spaceCharacter = ' ';
        const fullName = searchStr
          .split(spaceCharacter)
          .map((data) => data.trim())
          .filter((data) => !!data);
        const firstName = fullName[0];
        const lastName = fullName[1];
        if (fullName.length !== 2 || !firstName || !lastName) {
          displayPopupMessageInformation(`Please enter a full name in the format 'firstname lastname'`);
          break;
        }
        const queryParams = `first_name:${firstName} last_name:${lastName}`;
        query = {
          query: queryParams,
          status: 'any',
        };
        setLoading(true);

        return dispatch(shopifyGetCustomerOrders(query));
      case searchActions.AMAZON_ORDER_NUMBER:
        if (!searchStr) {
          displayPopupMessageInformation('Please enter an order number');
          break;
        }
        setLoading(true);
        try {
          return dispatch(amazonGetOrderByNumber(simplrAccountPtrId, searchStr));
        } catch (error) {
          handleError({ error });
        }
        break;
      case searchActions.RBI_EMAIL:
        if (!emailRegex.test(searchStr)) {
          displayPopupMessageInformation('Please enter a valid email address');
          break;
        }

        return dispatch(searchRbiOrdersByEmail(simplrAccountPtrId, searchStr));

      case searchActions.RBI_ID:
        /**
         * NOTE(losch|SPD-11278|2024-11-11): str.trim case prevents API error from user entering
         * string with all spaces.
         */
        if (!searchStr || !searchStr.trim()) {
          displayPopupMessageInformation('Please enter an order ID');
          break;
        }

        return dispatch(searchRbiOrdersById(simplrAccountPtrId, searchStr.trim()));

      case searchActions.RBI_CARD_ID:
        if (!searchStr) {
          displayPopupMessageInformation('Please enter a valid card ID');
          break;
        }

        return dispatch(searchRbiOrdersByCardId(simplrAccountPtrId, searchStr));
      case searchActions.SALESFORCE_ORDER_EMAIL:
        if (!emailRegex.test(searchStr)) {
          displayPopupMessageInformation('Please enter a valid email address');
          break;
        }
        setLoading(true);

        return dispatch(searchSalesforceOrdersByEmail(simplrAccountPtrId, searchStr.trim()));
      case searchActions.SALESFORCE_ORDER_ID:
        setLoading(true);

        return dispatch(searchSalesforceOrdersById(simplrAccountPtrId, searchStr.trim()));
      case searchActions.BIG_COMMERCE_ORDER_EMAIL:
        if (!searchStr || !searchStr.trim()) {
          displayPopupMessageInformation('Please enter a valid email address');
          break;
        }
        setLoading(true);

        return dispatch(bigCommerceFindOrders('email', searchStr.trim()));
      case searchActions.BIG_COMMERCE_ORDER_ID:
        if (!searchStr || !searchStr.trim()) {
          displayPopupMessageInformation('Please enter a valid order number');
          break;
        }
        setLoading(true);

        return dispatch(bigCommerceFindOrders('orderNumber', searchStr.trim()));
      case searchActions.REVOLVE_ORDER_EMAIL:
        if (!searchStr || !searchStr.trim()) {
          displayPopupMessageInformation('Please enter a valid email address');
          break;
        }
        setLoading(true);

        return dispatch(revolveFindOrders('email', searchStr.trim()));
      case searchActions.REVOLVE_ORDER_ID:
        if (!searchStr || !searchStr.trim()) {
          displayPopupMessageInformation('Please enter a valid order number');
          break;
        }
        setLoading(true);

        return dispatch(revolveFindOrders('invoice', searchStr.trim()));
      case searchActions.COMMON_ORDER_EMAIL:
        if (!searchStr || !searchStr.trim() || !emailRegex.test(searchStr)) {
          displayPopupMessageInformation('Please enter a valid email');
          break;
        }

        return dispatch(searchOrders({ customerEmail: searchStr.trim() }));
      case searchActions.COMMON_ORDER_NUMBER:
        if (!searchStr || !searchStr.trim()) {
          displayPopupMessageInformation('Please enter a valid order number');
          break;
        }

        return dispatch(searchOrders({ orderNumber: searchStr.trim() }));
      case searchActions.COMMON_ORDER_PHONE_NUMBER:
        if (!searchStr || !searchStr.trim()) {
          displayPopupMessageInformation('Please enter a valid phone number');
          break;
        }

        return dispatch(searchOrders({ customerPhone: searchStr.trim() }));
      case searchActions.COMMON_ORDER_DYNAMIC_INPUT_0:
      case searchActions.COMMON_ORDER_DYNAMIC_INPUT_1:
      case searchActions.COMMON_ORDER_DYNAMIC_INPUT_2:
        // validation handled inside DTDynamicSearchBar
        return dispatch(searchOrders(trimObjectValues(dynamicOrderSearchValues)));
      default:
        handleError({ customMessage: `Unknown action: ${selectedSearchAction}` });
    }
  }, [dispatch, orderSearchQuery, selectedSearchAction, setLoading, simplrAccountPtrId, dynamicOrderSearchValues]);

  useEffect(() => {
    dispatch(selectOrderingSystem(chooseOrderingSystemForSelectedAction(selectedSearchAction)));

    // sets default value for dynamic integrations
    if (isEmailRequiredForDynamicIntegration && defaultEmail) {
      dispatch(
        setDynamicOrderSearchValue({
          paramName: CUSTOMER_EMAIL_SEARCH_INPUT,
          paramValue: defaultEmail,
        })
      );
    }
  }, [selectedSearchAction, isEmailRequiredForDynamicIntegration, defaultEmail, dispatch]);

  useEffect(() => {
    // sets default value for normal searches
    if (defaultSearchValue && !orderSearchQuery) {
      dispatch(setSearchOrdersQuery(defaultSearchValue));
    }
  }, [defaultSearchValue, orderSearchQuery, dispatch]);

  useEffect(() => {
    if (orderSearchQuery || (isDynamicIntegrationEmailPopulated && isEmailOnlyInputForDynamicIntegration)) {
      handleSearchOrders();
    }
  }, [orderSearchQuery, handleSearchOrders, isEmailOnlyInputForDynamicIntegration, isDynamicIntegrationEmailPopulated]);

  const handleSearchSubmit = (value) => {
    dispatch(setSearchOrdersQuery(value));
  };

  const handleActionSelection = async (event) => {
    // clear old data
    dispatch(clearSelectedItems());
    dispatch(selectOrder({}));
    dispatch(clearExtraLastMileInfo());

    const searchAction = event.target.value;
    await trackOrderSearchSetAction({ action: searchAction });
    await dispatch(setSearchOrdersAction(searchAction));
  };

  const handleBackButtonClick = () => dispatch(returnToPreviousMacroWizardPath());

  return (
    <Container>
      <HeaderContainer id="HeaderContainer">
        {content?.title && <Title>{content.title}</Title>}
        {content?.subText && <SubTitle>{content.subText}</SubTitle>}
        <SearchOrdersBox>
          <SearchInputContainer>
            <SearchPageSelect onSelectAction={handleActionSelection} />
          </SearchInputContainer>
          <SearchInputContainer>
            {isDynamicIntegrationSelected ? (
              <DTDynamicSearchBar onSearchSubmit={handleSearchOrders} />
            ) : (
              <DTSearchInput
                placeholder="Search keywords"
                messageImg={false}
                searchType={selectedSearchAction}
                searchValue={orderSearchQuery}
                defaultValue={defaultSearchValue}
                onSearchSubmit={handleSearchSubmit}
              />
            )}
          </SearchInputContainer>
          <SpaceHolder />
        </SearchOrdersBox>
      </HeaderContainer>
      <SearchResultsContainer>
        <LoadingStateWrapper
          data={ordersSearch.orders}
          isPending={isLoading}
          loadingLabel={'Searching Orders...'}
          noDataMessage={
            <NoResultsWrapper key={'noOrdersFound'}>
              <Title>No orders found</Title>
              <NoSearchResults />
            </NoResultsWrapper>
          }
          introMessage={<SearchIntro />}
        >
          <LoadingStateContextConsumer>
            {() => {
              return (
                <Loader visible={isLoading}>
                  <SearchOrderContent id="SearchOrderContent">
                    <SearchAndResults id="SearchAndResults">
                      <ResultsSection id="ResultsSection">
                        <SearchResults />
                      </ResultsSection>
                    </SearchAndResults>
                  </SearchOrderContent>
                </Loader>
              );
            }}
          </LoadingStateContextConsumer>
        </LoadingStateWrapper>
      </SearchResultsContainer>

      <ActionsButtonsWrapper key={'navigation'}>
        <SearchPageNextButton
          active={isNextButtonActive}
          disabled={!isNextButtonActive}
          type="submit"
          onClick={onNextButtonClick}
          isHidden={hideNextButton}
        >
          NEXT
        </SearchPageNextButton>
        <BackButton onClick={handleBackButtonClick}>BACK</BackButton>
      </ActionsButtonsWrapper>
    </Container>
  );
};
